import { trapFocus, removeTrapFocus } from '@theme/focus';

const MODAL_COMPONENT_ID = 'ctr-account-sign-in';
const OPEN_SELECTOR = '[data-ctr-account-sign-in-open]';

/** @type {HTMLElement | null} */
let lastTrigger = null;

/**
 * @returns {import('./dialog.js').DialogComponent | null}
 */
function getModalComponent() {
  const element = document.getElementById(MODAL_COMPONENT_ID);
  if (!element || !('showDialog' in element)) return null;
  return /** @type {import('./dialog.js').DialogComponent} */ (element);
}

/**
 * @returns {HTMLDialogElement | null}
 */
function getNativeDialog() {
  const dialog = document.getElementById('ctr-account-sign-in-dialog');
  return dialog instanceof HTMLDialogElement ? dialog : null;
}

function closeMobileNav() {
  document.querySelector('.custom-header')?.classList.remove('custom-header--active');
  document.querySelector('header-drawer')?.close?.();
}

/**
 * @param {HTMLElement} trigger
 */
function openModal(trigger) {
  const modal = getModalComponent();
  const nativeDialog = getNativeDialog();
  if (!modal && !nativeDialog) return;

  lastTrigger = trigger;
  closeMobileNav();
  if (modal) {
    modal.showDialog();
  } else if (!nativeDialog.open) {
    nativeDialog.showModal();
    handleDialogOpen();
  }
  document.dispatchEvent(new CustomEvent('ctr-account-sign-in:open'));
}

function handleOpenClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const pendingTelehealth = target.closest('[data-ctr-account-telehealth-pending]');
  if (pendingTelehealth) {
    event.preventDefault();
    return;
  }

  const trigger = target.closest(OPEN_SELECTOR);
  if (!trigger || !(trigger instanceof HTMLElement)) return;

  event.preventDefault();
  openModal(trigger);
}

function handleDialogOpen() {
  const modal = getModalComponent();
  const dialog = modal?.refs?.dialog || getNativeDialog();
  if (!(dialog instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    trapFocus(dialog);
    const closeButton = dialog.querySelector('.ctr-account-sign-in__close');
    if (closeButton instanceof HTMLElement) {
      closeButton.focus();
    }
  });
}

function handleDialogClose() {
  removeTrapFocus();
  if (lastTrigger instanceof HTMLElement) {
    lastTrigger.focus();
    lastTrigger = null;
  }
}

function closeNativeDialog() {
  const dialog = getNativeDialog();
  if (dialog?.open) {
    dialog.close();
  }
}

/**
 * @param {MouseEvent} event
 */
function handleNativeDialogClick(event) {
  const dialog = getNativeDialog();
  if (event.target === dialog) {
    closeNativeDialog();
  }
}

function initAccountSignInModal() {
  const modal = getModalComponent();
  const dialog = getNativeDialog();
  if (!modal && !dialog) return;

  document.addEventListener('click', handleOpenClick);
  modal?.addEventListener('dialog:open', handleDialogOpen);
  modal?.addEventListener('dialog:close', handleDialogClose);
  dialog?.addEventListener('close', handleDialogClose);
  dialog?.addEventListener('click', handleNativeDialogClick);
  dialog?.querySelector('.ctr-account-sign-in__close')?.addEventListener('click', closeNativeDialog);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAccountSignInModal);
} else {
  initAccountSignInModal();
}
